import { Box, Skeleton } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import type { GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { periverseTheme } from '@peritasai/ui-shared';
import { Card, CardContent } from './Card';
import { Typography } from './Typography';

const DEFAULT_ROW_HEIGHT = 76;
const DEFAULT_HEADER_HEIGHT = 56;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

interface NoRowsOverlayProps {
  hasSearch: boolean;
  emptyMessage: string;
  searchEmptyTitle: string;
  searchEmptyMessage: string;
}

const NoRowsOverlay = ({
  hasSearch,
  emptyMessage,
  searchEmptyTitle,
  searchEmptyMessage,
}: NoRowsOverlayProps) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {hasSearch ? (
        <>
          <Typography variant="h6" sx={{ color: 'text.primary', mb: 0.5 }}>
            {searchEmptyTitle}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {searchEmptyMessage}
          </Typography>
        </>
      ) : (
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {emptyMessage}
        </Typography>
      )}
    </Box>
  </Box>
);

const LoadingSkeleton = ({
  rowCount = 10,
  columnCount = 5,
}: {
  rowCount?: number;
  columnCount?: number;
}) => (
  <Box sx={{ width: '100%', height: '100%', backgroundColor: 'background.paper' }}>
    {Array.from({ length: rowCount }).map((_, i) => (
      <Box
        key={`skeleton-row-${i}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: DEFAULT_ROW_HEIGHT,
          borderBottom: `1px solid ${periverseTheme.palette.divider}`,
          px: 2,
          gap: 2,
        }}
      >
        {Array.from({ length: columnCount }).map((_, j) => (
          <Box key={`skeleton-col-${i}-${j}`} sx={{ flex: 1 }}>
            <Skeleton
              animation="wave"
              variant="rounded"
              width={`${Math.round(70 * (0.6 + (i * 7 + j * 3) % 4 * 0.1))}%`}
              height={8}
              sx={{ borderRadius: '24px' }}
            />
          </Box>
        ))}
      </Box>
    ))}
  </Box>
);

export interface DataTableProps<T extends object> {
  rows: T[];
  columns: GridColDef<T>[];
  getRowId: (row: T) => string;
  rowCount: number;
  isLoading: boolean;
  hasSearch?: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  searchEmptyTitle?: string;
  searchEmptyMessage?: string;
  pageSizeOptions?: number[];
  rowHeight?: number;
  columnHeaderHeight?: number;
  skeletonColumnCount?: number;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  sortingMode?: 'server' | 'client';
}

export const DataTable = <T extends object>({
  rows,
  columns,
  getRowId,
  rowCount,
  isLoading,
  hasSearch = false,
  paginationModel,
  onPaginationModelChange,
  onRowClick,
  emptyMessage = 'No data found.',
  searchEmptyTitle = 'No Matching Results',
  searchEmptyMessage = 'Check your spelling or try more general keywords.',
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  rowHeight = DEFAULT_ROW_HEIGHT,
  columnHeaderHeight = DEFAULT_HEADER_HEIGHT,
  skeletonColumnCount = 5,
  sortModel,
  onSortModelChange,
  sortingMode = 'client',
}: DataTableProps<T>) => (
  <Card>
    <CardContent sx={{ padding: '8px 16px' }}>
      <Box sx={{ width: '100%', position: 'relative', height: 'calc(100vh - 320px)', minHeight: 300 }}>
        <DataGridPremium
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          rowHeight={rowHeight}
          columnHeaderHeight={columnHeaderHeight}
          loading={isLoading}
          sortingMode={sortingMode}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={pageSizeOptions}
          rowCount={rowCount}
          onRowClick={onRowClick ? (params) => onRowClick(params.row as T) : undefined}
          slots={{
            loadingOverlay: () => <LoadingSkeleton rowCount={10} columnCount={skeletonColumnCount} />,
            noRowsOverlay: () => (
              <NoRowsOverlay
                hasSearch={hasSearch}
                emptyMessage={emptyMessage}
                searchEmptyTitle={searchEmptyTitle}
                searchEmptyMessage={searchEmptyMessage}
              />
            ),
          }}
          sx={{
            height: '100%',
            border: 'none',
            backgroundColor: periverseTheme.palette.background.paper,
            boxShadow: 'none',
            '--DataGrid-overlayHeight': !rows.length && !isLoading ? '100%' : undefined,
            '& .last-column-header': { borderRight: 'none' },
            '& .last-column-header .MuiDataGrid-columnSeparator': { display: 'none' },
            '&&.MuiDataGrid-root .MuiDataGrid-columnHeader': {
              backgroundColor: periverseTheme.palette.background.paper,
              color: periverseTheme.palette.text.secondary,
              fontSize: periverseTheme.typography.tableHeader.fontSize,
              fontWeight: periverseTheme.typography.tableHeader.fontWeight,
              lineHeight: periverseTheme.typography.tableHeader.lineHeight,
              letterSpacing: periverseTheme.typography.tableHeader.letterSpacing,
              fontFamily: periverseTheme.typography.tableHeader.fontFamily,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: periverseTheme.palette.text.secondary,
              fontSize: periverseTheme.typography.tableHeader.fontSize,
              fontWeight: periverseTheme.typography.tableHeader.fontWeight,
              lineHeight: periverseTheme.typography.tableHeader.lineHeight,
              letterSpacing: periverseTheme.typography.tableHeader.letterSpacing,
              fontFamily: periverseTheme.typography.tableHeader.fontFamily,
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': { flexGrow: 1 },
            '& .MuiDataGrid-columnHeadersInner': { borderRight: 'none' },
            '& .MuiDataGrid-cell': {
              padding: '12px 8px',
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              fontSize: periverseTheme.typography.tableCell.fontSize,
              fontWeight: periverseTheme.typography.tableCell.fontWeight,
              lineHeight: periverseTheme.typography.tableCell.lineHeight,
              letterSpacing: periverseTheme.typography.tableCell.letterSpacing,
              fontFamily: periverseTheme.typography.tableCell.fontFamily,
              color: periverseTheme.palette.text.primary,
            },
            '& .MuiDataGrid-cell > *': { minWidth: 0, width: '100%' },
            '& .MuiDataGrid-cellContent': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              width: '100%',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus-visible': { outline: 'none' },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus-visible': { outline: 'none' },
            '& .MuiDataGrid-row:focus-within': { outline: 'none' },
            '& .MuiDataGrid-cell *:focus-visible': { outline: 'none' },
            '& .MuiDataGrid-row': { cursor: onRowClick ? 'pointer' : 'default' },
            '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
            '& .MuiDataGrid-row--selected': { backgroundColor: `${periverseTheme.palette.primary.main}0A` },
            '& .MuiDataGrid-row--selected:hover': { backgroundColor: `${periverseTheme.palette.primary.main}14` },
            '& .MuiDataGrid-sortIcon': {
              color: periverseTheme.palette.primary.main,
              fontSize: '1.2rem',
              display: isLoading || !rows.length ? 'none' : undefined,
            },
            '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon': {
              color: periverseTheme.palette.text.secondary,
              display: isLoading || !rows.length ? 'none' : undefined,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${periverseTheme.palette.divider}`,
              opacity: isLoading ? 0.38 : 0.87,
              pointerEvents: isLoading ? 'none' : 'auto',
            },
            '& .MuiTablePagination-selectLabel': {
              opacity: isLoading ? 0.38 : 0.6,
              fontSize: periverseTheme.typography.tableCell.fontSize,
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: periverseTheme.typography.tableCell.fontSize,
            },
            '& .MuiTablePagination-select': {
              fontSize: periverseTheme.typography.tableCell.fontSize,
            },
          }}
        />
      </Box>
    </CardContent>
  </Card>
);
